import {Component, computed, DestroyRef, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {EmployeeRank} from '../../../../classes/models/employee/EmployeeRank';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {EmployeeRankEnum, rankValues} from '../../../../classes/enums/EmployeeRankEnum';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {EmployeeRankService} from '../../../../services/http/employee/employee-rank.service';
import {form} from '@angular/forms/signals';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Form model for employee rank data. */
interface EmployeeRankFormModel {
  rank: EmployeeRankEnum;
  startDate: string;
}

/** Data required to open the employee rank form modal. */
export interface EmployeeRankFormData {
  employeePositionId: EmployeePositionId;
  previousEmployeeRank?: EmployeeRank;
  weekExists?: boolean;
}

/**
 * This component handles creating or updating an employee rank.
 */
@Component({
  selector: 'app-employee-rank-form',
  templateUrl: './employee-rank-form.component.html',
  styleUrls: ['./employee-rank-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class EmployeeRankFormComponent {

  /** A modal component of creating an employee rank. */
  modalComponent = viewChild<ModalComponent>('modalComponent');
  /** Identifiers of an employee and position. */
  employeePositionId = signal<EmployeePositionId>(undefined);
  /** Previous employee rank. */
  previousEmployeeRank = signal<EmployeeRank | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  weekExists = signal(false);

  /** Signal form model derived from previous rank or defaulting to Rank_1 and empty start date. */
  formModel = linkedSignal<EmployeeRankFormModel>(() => ({
    rank: this.previousEmployeeRank()?.rank ?? EmployeeRankEnum.Rank_1,
    startDate: this.previousEmployeeRank()?.startDate ?? ''
  }));
  /** Signal form for employee rank data. */
  employeeRankForm = form(this.formModel);
  /** `True` if request sending to backend is in progress. */
  loading = signal(false);
  /** The first date from which employee rank data is allowed to be edited. */
  editDate = inject(DataService).editDate;
  /** Values of all employee ranks. */
  ranks = rankValues();
  /** Computed validity: form is submittable when start date is provided and not loading. */
  isFormValid = computed(() => !!this.formModel().startDate && !this.loading());

  private employeeRankService = inject(EmployeeRankService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);

  static open(modalService: NgbModal, data: EmployeeRankFormData): NgbModalRef {
    const modalRef = modalService.open(EmployeeRankFormComponent);
    const instance = modalRef.componentInstance as EmployeeRankFormComponent;
    instance.employeePositionId.set(data.employeePositionId);
    if (data.previousEmployeeRank !== undefined) {
      instance.previousEmployeeRank.set(data.previousEmployeeRank);
    }
    if (data.weekExists !== undefined) {
      instance.weekExists.set(data.weekExists);
    }
    return modalRef;
  }

  /** Updates the start date in the form model. */
  updateStartDate(date: string): void {
    this.formModel.update(m => ({...m, startDate: date}));
  }

  /**
   * Send request to backend to create or update an employee rank.
   */
  onSubmit(event?: Event): void {
    event?.preventDefault();
    this.loading.set(true);
    const model = this.formModel();
    const employeeRank = new EmployeeRank(model.rank, model.startDate);

    if (this.weekExists()) {
      this.editEmployeeRank(employeeRank);
    } else {
      this.addEmployeeRank(employeeRank);
    }
  }

  private addEmployeeRank(employeeRank: EmployeeRank): void {
    this.employeeRankService.addEmployeeRank(this.employeePositionId(), employeeRank)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()!.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Employee rank successfully created');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editEmployeeRank(employeeRank: EmployeeRank): void {
    this.employeeRankService.editEmployeeRank(this.employeePositionId(), employeeRank)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                    () => {
                      this.modalComponent()!.activeModal.close();
                      this.loading.set(false);
                      this.toastr.success('Employee rank successfully updated');
                      this.ruleCheckerService.check();
                    },
                    error => {
                      this.loading.set(false);
                      this.toastr.error(error.error.message);
                    }
            );
  }
}